import type {MessageInitShape} from "@bufbuild/protobuf";
import {
	backdropOptionsOf, backdropTemplateOf, sponsorOptionsOf, takeoverTemplateKeyOf,
} from "../sponsor/backdropOptionsMessages";
import type {Brand} from "../gen/kitecorp/transferify/storefront/v1/brand_pb";
import type {
	PageTheme as PageThemeMessage,
	SetPageThemeRequestSchema,
} from "../gen/kitecorp/transferify/storefront/v1/page_theme_pb";
import type {CustomerBrand} from "./AccountBrandApi";
import type {PageTheme, PageThemeUpdate} from "./AccountThemeApi";

/**
 * The storefront answers of the brand and page theme services (kitecorp/transferify-ui#3956), mapped back onto the
 * REST-shaped {@link CustomerBrand} and {@link PageTheme} the sheets read. Pure functions of one message each; a
 * rejected call never reaches them.
 *
 * An optional field the message does not carry is the `null` the REST body printed for it.
 */

/** A brand answer; no message is a brand with nothing set. */
export function brandOf(message: Brand | undefined): CustomerBrand {
	return {
		logoUrl: message?.logoUrl ?? null,
		primaryColor: message?.primaryColor ?? null,
		onPrimaryColor: message?.onPrimaryColor ?? null,
	};
}

/** A theme answer; no message is a theme nobody has saved, and an unspecified template is none. */
export function pageThemeOf(message: PageThemeMessage | undefined): PageTheme {
	return {
		enabled: message?.enabled ?? false,
		template: message === undefined ? null : takeoverTemplateKeyOf(message.template),
		options: sponsorOptionsOf(message?.options),
		images: (message?.images ?? []).map(image => ({
			assetId: image.assetId, url: image.imageUrl, attached: image.attached,
		})),
	};
}

/** The save request for a theme update; options are left out of it for a treatment that takes none. */
export function pageThemeRequestOf(update: PageThemeUpdate): MessageInitShape<typeof SetPageThemeRequestSchema> {
	return {
		template: backdropTemplateOf(update.template),
		options: backdropOptionsOf(update.options),
		enabled: update.enabled,
		assetIds: update.assetIds,
	};
}
